import { useEffect } from "react";
import { Button, Row, Typography, Spin, message, Card } from "antd";
import {
  LectureCard,
  AttendanceCard,
  PendingCard,
  CoachingCard,
  EmptyCard,
} from "../../component";
import "./Dashboard.less";
import { useGetUpcomingClassesQuery } from "../../redux/services/class";
import { useGetUpcomingAssignmentsQuery } from "../../redux/services/assignment";
import {
  useGetCurrentClassAttendanceQuery,
  useGetAttendanceByCoachingQuery,
} from "../../redux/services/attendance";
import {
  setCurrentClassCoachingId,
  setCurrentClassid,
} from "../../redux/features/authSlice";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { ROUTE_ASSIGNMENTS } from "../../constant/routes";
import { LectureCover } from "../../assets/images";

const { Title } = Typography;

const Dashboard = () => {
  const dispatch = useDispatch();
  const { student_id } = useSelector((state) => state.auth);

  // Queries
  const { data: upcomingClasses, isLoading: classLoading } =
    useGetUpcomingClassesQuery({ student_id });

  const { data: pendingAssignments, isLoading: assignmentLoading } =
    useGetUpcomingAssignmentsQuery({ student_id });

  const {
    data: currentClassAttendance,
    isLoading: attendanceLoading,
    error: attendanceError,
  } = useGetCurrentClassAttendanceQuery(student_id);

  const {
    data: attendanceByCoaching,
    isLoading: attendanceByCoachingLoading,
    error: attendanceByCoachingError,
    refetch: refetchAttendanceByCoaching,
  } = useGetAttendanceByCoachingQuery(student_id);

  // Set current class data in redux store
  useEffect(() => {
    if (currentClassAttendance?.data?.length > 0) {
      const [attendance] = currentClassAttendance.data;
      dispatch(setCurrentClassid(attendance.class_id));
      dispatch(setCurrentClassCoachingId(attendance.coaching_id));
    }
  }, [currentClassAttendance, dispatch]);

  // Show error messages if needed
  useEffect(() => {
    if (attendanceError || attendanceByCoachingError) {
      message.error("Error fetching attendance data");
    }
  }, [attendanceError, attendanceByCoachingError]);

  // Render component sections
  const renderAttendanceSection = () => {
    // if (!currentClassAttendance?.data?.length) return null;

    return (
      <div className='attendance' style={{ marginBottom: "30px" }}>
        <Title className='title' style={{ marginBottom: "30px" }}>
          Attendance
        </Title>
        {attendanceLoading ? (
          <>
            <AttendanceCard loading={true} />
          </>
        ) : currentClassAttendance?.data?.length > 0 ? (
          currentClassAttendance.data.map((attendance, index) => (
            <AttendanceCard
              key={index}
              centrelogo={attendance.coaching_logo}
              location={attendance.coaching_name}
              classname={attendance.class_name}
              teachername={attendance.teacher_name}
              timing={`${attendance.date} | ${attendance.time}`}
              refetchAttendanceByCoaching={refetchAttendanceByCoaching}
            />
          ))
        ) : (
          <EmptyCard text='No Current Class' />
        )}
      </div>
    );
  };

  const renderUpcomingClassesSection = () => {
    return (
      <div className='upcoming-classes'>
        <Title className='title'>Upcoming Classes</Title>
        <div className='card-scroll-container'>
          {classLoading ? (
            // Show loading cards when data is loading
            <>
              <LectureCard loading={true} />
              <LectureCard loading={true} />
            </>
          ) : upcomingClasses?.data?.length > 0 ? (
            // Show actual data when available
            upcomingClasses.data.map((lecture) => (
              <LectureCard
                key={lecture.lecture_id}
                lectureName={lecture.lecture_name}
                dateTime={`${lecture.date} at ${lecture.time}`}
                lectureImage={lecture.lecture_image}
                classUrl={lecture.class_url}
                loading={false}
              />
            ))
          ) : (
            // Show empty card only when not loading and no data
            <EmptyCard text='No Upcoming Classes' />
          )}
        </div>
      </div>
    );
  };

  const renderPendingAssignmentsSection = () => {
    return (
      <div className='pending-assignments'>
        <Row justify='space-between'>
          <Title className='title'>Pending Assignments</Title>
          <Button
            type='link'
            className='view-all-button'
            disabled={!pendingAssignments?.data?.length}
          >
            <Link to={ROUTE_ASSIGNMENTS}>View all</Link>
          </Button>
        </Row>
        <div className='card-scroll-container'>
          {assignmentLoading ? (
            <>
              <PendingCard loading={true} />
              <PendingCard loading={true} />
            </>
          ) : pendingAssignments?.data?.length > 0 ? (
            pendingAssignments.data.map((assignment) => (
              <PendingCard
                key={assignment.assignment_id}
                subjectname={assignment.assignment_name}
                duedate={`${assignment.end_date} at ${assignment.end_time}`}
                lectureImage={assignment.assignment_image || LectureCover}
              />
            ))
          ) : (
            <EmptyCard text='No Pending Assignments' />
          )}
        </div>
      </div>
    );
  };

  const renderAllCoachingSection = () => {
    return (
      <div className='all-coaching' style={{ marginTop: "30px" }}>
        <Title className='title'>All Coaching</Title>
        <div style={{ marginTop: "20px" }}>
          {attendanceByCoachingLoading ? (
            <>
              <CoachingCard loading={true} />
              <CoachingCard loading={true} />
            </>
          ) : attendanceByCoaching?.data?.length > 0 ? (
            attendanceByCoaching.data.map((coachingItem, index) => (
              <CoachingCard
                key={index}
                centrelogo={coachingItem.coaching_logo}
                present={coachingItem.attended_classes}
                totalclasses={coachingItem.total_classes}
                coachingName={coachingItem.coaching_name}
              />
            ))
          ) : (
            <EmptyCard text='No Coaching Available' />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className='dashboard-container'>
      {renderAttendanceSection()}
      {renderUpcomingClassesSection()}
      {renderPendingAssignmentsSection()}
      {renderAllCoachingSection()}
    </div>
  );
};

export default Dashboard;
